/**
 * Which strategic resources each city of a civilisation has (ADR-0063).
 *
 * A deposit counts when it is revealed to its owner, lies inside that owner's borders,
 * and is joined to a city: by roads (a city's own tile always has one) and rivers
 * through the civilisation's own or unclaimed land, or — once it knows
 * `rules.resources.seaTech` — by sea, which joins every coastal city it has. A resource
 * traded in under a deal reaches every city.
 *
 * Computed afresh on every call — one pass over the map and a union-find over the few
 * hundred tiles that carry a road or river — rather than cached, because roads, borders,
 * cities and deals all change it and a stale answer is worse than a slow one. Callers
 * that ask about many cities (the AI choosing production) ask once and pass it down.
 * Resources are a bit mask by content id; the schema caps them at 31.
 */

import type { Content, UnitDef } from '../../../content/schema/index.js';
import { citiesOf, cityIndex } from '../cities/index.js';
import { knows } from '../research/knowledge.js';
import { neighborsByDirection } from '../state/grid.js';
import { NONE, type City, type GameState, type Id, type ProductionItem } from '../state/types.js';
import { resourceKnown } from './known.js';

export interface Deposit {
  tile: number;
  resource: number;
  /** Joined to one of the civilisation's cities. */
  connected: boolean;
}

export interface ResourceAccess {
  /** What each city has, by city id. */
  cities: Map<Id, number>;
  /** What the civilisation's own connected deposits give it anywhere. */
  own: number;
  /** What it has under deals. */
  traded: number;
  /** Every revealed strategic deposit inside its borders. */
  deposits: Deposit[];
}

const EMPTY: ResourceAccess = { cities: new Map(), own: 0, traded: 0, deposits: [] };

function find(parent: Int32Array, i: number): number {
  while (parent[i] !== i) {
    parent[i] = parent[parent[i]!]!;
    i = parent[i]!;
  }
  return i;
}

function union(parent: Int32Array, a: number, b: number): void {
  const x = find(parent, a);
  const y = find(parent, b);
  if (x !== y) parent[Math.max(x, y)] = Math.min(x, y);
}

/** Resources traded to a civilisation and still running. */
export function tradedIn(state: GameState, civId: Id): number {
  let mask = 0;
  for (const grant of state.grants) if (grant.to === civId && grant.until > state.meta.turn) mask |= 1 << grant.resource;
  return mask;
}

export function resourceAccess(state: GameState, content: Content, civId: Id): ResourceAccess {
  const civ = state.civs[civId];
  if (!civ) return EMPTY;
  const map = state.map;
  const n = map.width * map.height;
  const sea = n; // one extra node: the sea, once the civilisation can sail it
  const parent = new Int32Array(n + 1);
  for (let i = 0; i <= n; i++) parent[i] = i;

  const linkable = (i: number): boolean =>
    (map.owner[i] === civId || map.owner[i] === NONE) && !content.terrain.get(map.terrain[i]!).water;
  const node = (i: number): boolean => linkable(i) && (map.road[i] === 1 || map.rivers[i] !== 0);

  const around = new Int32Array(6);
  for (let i = 0; i < n; i++) {
    if (!node(i)) continue;
    neighborsByDirection(map, i, around);
    for (let d = 0; d < 6; d++) {
      const j = around[d]!;
      if (j < 0 || j < i || !node(j)) continue;
      // Two river tiles join only along the river; a road joins anything it touches.
      const riverLink = (map.rivers[i]! & (1 << d)) !== 0;
      if (map.road[i] === 1 || map.road[j] === 1 || riverLink) union(parent, i, j);
    }
  }

  const cities = citiesOf(state, civId);
  const seaTech = content.technologies.id(content.rules.resources.seaTech);
  if (knows(civ, seaTech)) {
    const lake = content.terrain.byKey.get('lake')?.id ?? -1;
    for (const city of cities) {
      const centre = cityIndex(map, city);
      neighborsByDirection(map, centre, around);
      for (const j of around) {
        if (j >= 0 && map.terrain[j] !== lake && content.terrain.get(map.terrain[j]!).water) {
          union(parent, centre, sea);
          break;
        }
      }
    }
  }

  const withCity = new Set(cities.map((c) => find(parent, cityIndex(map, c))));
  const found = new Map<number, number>();
  const deposits: Deposit[] = [];
  let own = 0;
  for (let i = 0; i < n; i++) {
    const resource = map.resource[i]!;
    if (resource === 0 || map.owner[i] !== civId) continue;
    if (content.resources.get(resource).class !== 'strategic' || !resourceKnown(content, civ, resource)) continue;
    const root = find(parent, i);
    const connected = node(i) && withCity.has(root);
    deposits.push({ tile: i, resource, connected });
    if (!connected) continue;
    found.set(root, (found.get(root) ?? 0) | (1 << resource));
    own |= 1 << resource;
  }

  const traded = tradedIn(state, civId);
  const perCity = new Map<Id, number>();
  for (const city of cities) perCity.set(city.id, (found.get(find(parent, cityIndex(map, city))) ?? 0) | traded);
  return { cities: perCity, own, traded, deposits };
}

/** What a civilisation's unit standing here has to draw on: its city's resources, or none. */
export function resourcesAt(state: GameState, access: ResourceAccess, index: number): number {
  for (const city of Object.values(state.cities)) {
    if (cityIndex(state.map, city) === index) return access.cities.get(city.id) ?? 0;
  }
  return 0;
}

/** The resources a unit needs that this mask lacks, in content order. */
export function missingFor(def: UnitDef, mask: number): number[] {
  return def.resourceIds.filter((r) => (mask & (1 << r)) === 0);
}

/**
 * The first resource a city lacks to build this, or -1. Pass `access` when asking about
 * several cities of one civilisation.
 */
export function resourceBlocker(
  state: GameState,
  content: Content,
  city: City,
  item: ProductionItem,
  access?: ResourceAccess,
): number {
  if (item.kind !== 'unit') return -1;
  const def = content.units.get(item.type);
  if (def.resourceIds.length === 0) return -1;
  const mask = (access ?? resourceAccess(state, content, city.owner)).cities.get(city.id) ?? 0;
  return missingFor(def, mask)[0] ?? -1;
}
